import posthog from "posthog-js";

// ============================================
// Vehicle Upload Events
// ============================================

export const trackVehicleUploadStarted = (metadata?: {
  source?: "drag_drop" | "file_picker" | "camera";
  fileType?: string;
  fileSize?: number;
}) => {
  posthog.capture("vehicle_upload_started", metadata);
};

export const trackVehicleUploadCompleted = (metadata?: {
  vehicleUploadId?: string;
  fileType?: string;
  fileSize?: number;
  uploadDurationMs?: number;
}) => {
  posthog.capture("vehicle_upload_completed", metadata);
};

export const trackVehicleUploadFailed = (metadata?: {
  error?: string;
  fileType?: string;
  fileSize?: number;
}) => {
  posthog.capture("vehicle_upload_failed", metadata);
};

// ============================================
// Wheel Selection Events
// ============================================

export const trackWheelSelected = (metadata: {
  wheelType: "catalog" | "custom";
  wheelId: string;
  wheelName?: string;
}) => {
  posthog.capture("wheel_selected", metadata);
};

export const trackWheelDeselected = () => {
  posthog.capture("wheel_deselected");
};

// ============================================
// Free Preview Events
// ============================================

export const trackFreePreviewStarted = (metadata?: {
  vehicleUploadId?: string;
  wheelId?: string;
  wheelType?: "catalog" | "custom";
}) => {
  posthog.capture("free_preview_started", metadata);
};

export const trackFreePreviewCompleted = (metadata?: {
  generationId?: string;
  durationMs?: number;
}) => {
  posthog.capture("free_preview_completed", metadata);
};

export const trackFreePreviewFailed = (metadata?: {
  generationId?: string;
  error?: string;
}) => {
  posthog.capture("free_preview_failed", metadata);
};

export const trackFreePreviewContinuationClicked = (metadata: {
  generationId?: string;
  remainingFreePreviews: number;
  source: "results_view" | "fullscreen_modal";
}) => {
  posthog.capture("free_preview_continuation_clicked", metadata);
};

export const trackFreePreviewComparisonViewed = (metadata: {
  generationIds: string[];
  vehicleUploadId?: string;
}) => {
  posthog.capture("free_preview_comparison_viewed", metadata);
};

export const trackFreePreviewWinnerSelected = (metadata: {
  generationId: string;
  previousGenerationId?: string;
  wheelType: "catalog" | "custom" | "deleted";
}) => {
  posthog.capture("free_preview_winner_selected", metadata);
};

// ============================================
// Generation Events (Paid)
// ============================================

export const trackGenerationStarted = (metadata?: {
  vehicleUploadId?: string;
  wheelId?: string;
  wheelType?: "catalog" | "custom";
  creditCost?: number;
}) => {
  posthog.capture("generation_started", metadata);
};

export const trackGenerationCompleted = (metadata?: {
  generationId?: string;
  durationMs?: number;
}) => {
  posthog.capture("generation_completed", metadata);
};

export const trackGenerationFailed = (metadata?: {
  generationId?: string;
  error?: string;
}) => {
  posthog.capture("generation_failed", metadata);
};

// ============================================
// Unlock Events
// ============================================

export const trackUnlockClicked = (metadata?: {
  generationId?: string;
  creditBalance?: number;
}) => {
  posthog.capture("unlock_clicked", metadata);
};

export const trackUnlockCompleted = (metadata?: {
  generationId?: string;
}) => {
  posthog.capture("unlock_completed", metadata);
};

// ============================================
// Mobile Navigation Events (Legacy - kept for backwards compatibility)
// ============================================

export const trackMobileStepChanged = (metadata: {
  fromStep: 1 | 2 | 3;
  toStep: 1 | 2 | 3;
  direction: "forward" | "back";
}) => {
  posthog.capture("mobile_step_changed", metadata);
};

// ============================================
// Single-Page Flow Events
// ============================================

export const trackMoreWheelsClicked = (metadata?: {
  selectedWheelId?: string;
  selectedWheelType?: "catalog" | "custom";
}) => {
  posthog.capture("more_wheels_clicked", metadata);
};

export const trackMoreWheelsDrawerToggled = (metadata?: {
  action: "open" | "close";
  reason?: "user" | "pagehide" | "unmount";
  selectedWheelId?: string;
  selectedWheelType?: "catalog" | "custom";
}) => {
  posthog.capture("more_wheels_drawer_toggled", metadata);
};

export const trackFullscreenResultViewed = (metadata?: {
  generationId?: string;
  isFreePreview?: boolean;
  isUnlocked?: boolean;
}) => {
  posthog.capture("fullscreen_result_viewed", metadata);
};

export const trackFullscreenResultClosed = (metadata?: {
  generationId?: string;
  action?: "close" | "try_another" | "download" | "share";
}) => {
  posthog.capture("fullscreen_result_closed", metadata);
};

export const trackShareClicked = (metadata?: {
  generationId?: string;
  shareMethod?: "native" | "copy_link";
}) => {
  posthog.capture("share_clicked", metadata);
};

// ============================================
// Page View Events
// ============================================

export const trackAppPageViewed = (metadata?: {
  hasExistingVehicle?: boolean;
  hasExistingGenerations?: boolean;
  isEligibleForFreePreview?: boolean;
  creditBalance?: number;
}) => {
  posthog.capture("app_page_viewed", metadata);
};

export const trackCreditsPageViewed = (metadata?: {
  isAuthenticated?: boolean;
  creditBalance?: number;
  hasPurchasedAnyPack?: boolean;
}) => {
  posthog.capture("credits_page_viewed", metadata);
};

export const trackGalleryPageViewed = (metadata?: {
  itemCount?: number;
}) => {
  posthog.capture("gallery_page_viewed", metadata);
};

export type GalleryCtaLocation =
  | "header"
  | "footer"
  | "home"
  | "home_after_hero"
  | "home_after_showcase"
  | "gallery"
  | "gallery_top"
  | "gallery_mid"
  | "gallery_bottom"
  | "gallery_empty";

export const trackGalleryCtaClicked = (metadata?: {
  location?: GalleryCtaLocation;
}) => {
  posthog.capture("gallery_cta_clicked", metadata);
};

// ============================================
// User Identification
// ============================================

export const identifyUser = (
  userId: string,
  traits?: {
    email?: string;
    name?: string;
    createdAt?: string;
    hasPurchasedAnyPack?: boolean;
    creditBalance?: number;
    isCompanyMember?: boolean;
  }
) => {
  posthog.identify(userId, traits);
};

export const resetUser = () => {
  posthog.reset();
};

// ============================================
// Purchase Events
// ============================================

export const trackCheckoutStarted = (metadata?: {
  packId?: string;
  packName?: string;
  price?: number;
  selectedGenerationId?: string;
  source?: string;
  previewNumber?: 1 | 2;
}) => {
  posthog.capture("checkout_started", metadata);
};

export const trackCheckoutCreationFailed = (metadata: {
  packId: string;
  selectedGenerationId?: string;
  source?: string;
  previewNumber?: 1 | 2;
  status: number;
  error: string;
}) => {
  posthog.capture("checkout_creation_failed", metadata);
};

export const trackFreePreviewOfferShown = (metadata: {
  generationId?: string;
  previewNumber: 1 | 2;
  remainingFreePreviews: number;
  source: "results_view" | "fullscreen_modal" | "free_preview_comparison";
  offerVariant?: string;
}) => {
  posthog.capture("free_preview_paid_offer_shown", metadata);
};

export const trackFreePreviewPaidCtaClicked = (metadata: {
  packId?: string;
  generationId?: string;
  previewNumber: 1 | 2;
  remainingFreePreviews: number;
  source: "results_view" | "fullscreen_modal" | "free_preview_comparison";
  offerVariant?: string;
}) => {
  posthog.capture("free_preview_paid_cta_clicked", metadata);
};

export const trackPurchaseCompleted = (metadata?: {
  packId?: string;
  packName?: string;
  price?: number;
  credits?: number;
}) => {
  posthog.capture("purchase_completed", metadata);
};

// ============================================
// Download Events
// ============================================

export const trackImageDownloaded = (metadata?: {
  generationId?: string;
  imageType?: "result" | "original";
  isFreePreview?: boolean;
  isUnlocked?: boolean;
}) => {
  posthog.capture("image_downloaded", metadata);
};

export const trackDownloadGateClicked = (metadata?: {
  generationId?: string;
  isFreePreview?: boolean;
  isUnlocked?: boolean;
  source?: "results_view" | "fullscreen_modal";
}) => {
  posthog.capture("download_gate_clicked", metadata);
};

export const trackUpgradePromptShown = (metadata?: {
  generationId?: string;
  trigger?: "after_generation" | "after_comparison" | "on_download" | "on_try_another";
  isFreePreview?: boolean;
  source?: "results_view" | "fullscreen_modal";
}) => {
  posthog.capture("upgrade_prompt_shown", metadata);
};

// ============================================
// Generation History Events
// ============================================

export const trackGenerationHistorySelected = (metadata?: {
  generationId?: string;
  isFreePreview?: boolean;
  isUnlocked?: boolean;
}) => {
  posthog.capture("generation_history_selected", metadata);
};

// ============================================
// Contact Form Events
// ============================================

export const trackContactFormSubmitted = (metadata?: {
  subject?: string;
  intent?: "general" | "dealer";
}) => {
  posthog.capture("contact_form_submitted", metadata);
};

export const trackDealerContactSubmitted = (metadata?: {
  subject?: string;
  shopName?: string;
}) => {
  posthog.capture("dealer_contact_submitted", metadata);
};

export const trackBlogCtaClicked = (metadata?: {
  slug?: string;
  location?: "article_bottom";
}) => {
  posthog.capture("blog_cta_clicked", metadata);
};

export const trackSeoLandingCtaClicked = (metadata?: {
  slug?: string;
  label?: string;
  location?: "hero" | "final";
}) => {
  posthog.capture("seo_landing_cta_clicked", metadata);
};

// ============================================
// Error Tracking
// ============================================

export const captureException = (error: unknown, metadata?: Record<string, unknown>) => {
  posthog.captureException(error, metadata);
};

// ============================================
// Wheel Guide Events
// ============================================

export const trackWheelGuideVehicleSelected = (metadata?: {
  make?: string;
  model?: string;
  year?: string;
}) => {
  posthog.capture("wheel_guide_vehicle_selected", metadata);
};
